import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import portalApi, { fmtINR, fmtQty } from "@/lib/portalApi";
import { catColor } from "@/pages/portal/PortalOrder";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { PackageCheck, Clock, ShieldCheck, XCircle, ShoppingBag, Loader2,
         FileText, ExternalLink, RotateCcw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
    submitted:        { label: "Submitted",         icon: Clock,       cls: "bg-amber-100 text-amber-800",     stage: 1 },
    confirmed:        { label: "Confirmed",         icon: ShieldCheck, cls: "bg-sky-100 text-sky-800",         stage: 2 },
    packing:          { label: "Packing",           icon: PackageCheck,cls: "bg-violet-100 text-violet-800",   stage: 3 },
    out_for_delivery: { label: "Out for Delivery",  icon: PackageCheck,cls: "bg-orange-100 text-orange-800",   stage: 4 },
    delivered:        { label: "Delivered",         icon: ShieldCheck, cls: "bg-emerald-100 text-emerald-800", stage: 5 },
    converted:        { label: "Invoiced",          icon: ShieldCheck, cls: "bg-emerald-100 text-emerald-800", stage: 5 },
    cancelled:        { label: "Cancelled",         icon: XCircle,     cls: "bg-red-100 text-red-800",         stage: 0 },
};
const STAGES = ["submitted", "confirmed", "packing", "out_for_delivery", "delivered"];

export default function PortalMyOrders() {
    const { client } = usePortalAuth();
    const [orders, setOrders] = useState(null);

    useEffect(() => {
        if (!client) return;
        portalApi.get("/orders").then(r => setOrders(r.data)).catch(() => setOrders([]));
    }, [client]);

    if (orders === null) return <div className="flex items-center justify-center py-24 text-slate-500">
        <Loader2 className="animate-spin mr-2" size={16}/> Loading your orders…
    </div>;

    return (
        <div className="space-y-6" data-testid="portal-my-orders">
            <div>
                <h1 className="text-3xl md:text-4xl font-black text-slate-900" style={{fontFamily:"'Chivo',sans-serif"}}>
                    My orders
                </h1>
                <p className="text-slate-500 mt-1">Orders you've placed through the portal · once HDB confirms one, it becomes an invoice</p>
            </div>

            {orders.length === 0 ? (
                <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center" data-testid="portal-no-orders">
                    <PackageCheck size={40} className="mx-auto text-slate-300 mb-3"/>
                    <div className="text-lg font-semibold text-slate-700">No orders yet</div>
                    <p className="text-sm text-slate-500 mt-1 mb-5">Start with your first order — we've made it quick.</p>
                    <Link to="/portal/order" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white font-bold">
                        <ShoppingBag size={16}/> Place your first order
                    </Link>
                </div>
            ) : (
                <div className="space-y-3">
                    {orders.map((o, i) => {
                        const s = STATUS[o.status] || STATUS.submitted;
                        const Icon = s.icon;
                        return (
                            <details key={o.id || i} className="bg-white rounded-2xl border border-slate-200 shadow-sm"
                                     data-testid={`portal-order-${i}`}>
                                <summary className="p-4 cursor-pointer flex items-center gap-4 flex-wrap">
                                    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${s.cls}`}>
                                        <Icon size={11}/> {s.label}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-slate-900">{o.line_items.length} items</div>
                                        <div className="text-xs text-slate-500">
                                            Placed {o.created_at?.slice(0,10)}
                                            {o.delivery_date_preference ? ` · preferred delivery ${o.delivery_date_preference}` : ""}
                                        </div>
                                        {o.converted_invoice_number && (
                                            <div className="mt-1 flex items-center gap-1 flex-wrap">
                                                <span className="text-[10px] font-bold text-emerald-800 bg-emerald-50 px-1.5 py-0.5 rounded uppercase tracking-wider"
                                                      data-testid={`portal-order-invoice-num-${i}`}>
                                                    Invoice · {o.converted_invoice_number}
                                                </span>
                                                {o.converted_invoice_id && (
                                                    <a href={`${BACKEND_URL}/api/portal/invoices/${o.converted_invoice_id}/pdf`}
                                                       target="_blank" rel="noopener noreferrer"
                                                       onClick={(e) => e.stopPropagation()}
                                                       className="text-[10px] font-bold text-white bg-slate-900 hover:bg-slate-800 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                                                       data-testid={`portal-order-view-invoice-${i}`}>
                                                        <FileText size={9}/> View PDF <ExternalLink size={8}/>
                                                    </a>
                                                )}
                                                {o.converted_invoice_id && (
                                                    <Link to={`/portal/order?reorder_id=${o.converted_invoice_id}`}
                                                          onClick={(e) => e.stopPropagation()}
                                                          className="text-[10px] font-bold text-emerald-800 bg-emerald-100 hover:bg-emerald-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                                                          data-testid={`portal-order-reorder-${i}`}>
                                                        <RotateCcw size={9}/> Reorder
                                                    </Link>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <div className="text-lg font-black font-mono text-slate-900">{fmtINR(o.grand_total)}</div>
                                        <div className={`text-[10px] uppercase tracking-wider ${o.converted_invoice_number ? "text-emerald-700 font-bold" : "text-slate-400"}`}>
                                            {o.converted_invoice_number ? "invoiced" : "estimated"}
                                        </div>
                                    </div>
                                </summary>
                                <div className="px-4 pb-4 pt-1 border-t border-slate-100">
                                    {/* Visual status stepper */}
                                    <StatusStepper current={o.status} history={o.status_history} />
                                    {/* Group past-order lines by category — same style as the invoice */}
                                    {(() => {
                                        const buckets = {};
                                        for (const l of o.line_items) {
                                            const k = l.category || "Uncategorised";
                                            (buckets[k] ||= []).push(l);
                                        }
                                        const entries = Object.entries(buckets).sort(([a], [b]) => a.localeCompare(b));
                                        return entries.map(([catName, catLines]) => {
                                            const col = catColor(catName);
                                            return (
                                            <div key={catName} className="mt-2 first:mt-0">
                                                <div className={`${col.bg} ${col.text} px-3 py-1.5 rounded-md flex items-center gap-2 text-xs font-black uppercase tracking-wide`}
                                                     style={{fontFamily:"'Chivo',sans-serif"}}>
                                                    <span>{catName}</span>
                                                    <span className="opacity-70 font-mono">· {catLines.length}</span>
                                                </div>
                                                <table className="w-full text-sm">
                                                    <tbody className="divide-y divide-slate-100">
                                                        {catLines.map((l, j) => (
                                                            <tr key={j}>
                                                                <td className="py-1.5 px-2">{l.name}</td>
                                                                <td className="py-1.5 text-right font-mono text-xs">{fmtQty(l.qty)} {l.unit}</td>
                                                                <td className="py-1.5 text-right font-mono text-xs text-slate-500">{fmtINR(l.rate)}</td>
                                                                <td className="py-1.5 text-right font-mono font-bold text-xs pr-2">{fmtINR(l.line_total)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        );});
                                    })()}
                                    {o.notes && (
                                        <div className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded-lg p-2 text-slate-700">
                                            <b>Notes:</b> {o.notes}
                                        </div>
                                    )}
                                </div>
                            </details>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

/**
 * StatusStepper — horizontal 5-stage tracker showing order lifecycle.
 * Highlights the current stage + all preceding stages. Cancelled orders
 * render a distinct red banner instead of the stepper.
 */
function StatusStepper({ current, history }) {
    if (current === "cancelled") {
        return (
            <div className="my-2 p-2 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs font-semibold flex items-center gap-2"
                 data-testid="portal-status-cancelled">
                <XCircle size={12}/> Order was cancelled
            </div>
        );
    }
    const idx = STAGES.indexOf(current);
    const currentIdx = idx === -1 ? (current === "converted" ? STAGES.length - 1 : 0) : idx;
    // Map history so each stage can show a timestamp
    const stampFor = {};
    (history || []).forEach(h => { stampFor[h.status] = h.at; });

    return (
        <div className="mt-1 mb-3 flex items-center gap-1" data-testid="portal-status-stepper">
            {STAGES.map((s, i) => {
                const meta = STATUS[s];
                const done = i <= currentIdx;
                const now  = i === currentIdx;
                return (
                    <div key={s} className="flex-1 flex items-center gap-1 min-w-0"
                         data-testid={`portal-stage-${s}${now ? "-active" : ""}`}>
                        <div className="flex flex-col items-center gap-0.5 min-w-0">
                            <div className={`w-6 h-6 rounded-full grid place-items-center text-[10px] font-black
                                            ${now ? "bg-emerald-600 text-white ring-4 ring-emerald-100"
                                              : done ? "bg-emerald-500 text-white"
                                              : "bg-slate-200 text-slate-500"}`}>
                                {i + 1}
                            </div>
                            <div className={`text-[9px] uppercase tracking-wide font-bold text-center leading-tight truncate max-w-[80px]
                                            ${now ? "text-emerald-800" : done ? "text-slate-700" : "text-slate-400"}`}>
                                {meta.label}
                            </div>
                            {stampFor[s] && (
                                <div className="text-[8px] text-slate-400 font-mono">
                                    {stampFor[s].slice(5, 10)}
                                </div>
                            )}
                        </div>
                        {i < STAGES.length - 1 && (
                            <div className={`h-0.5 flex-1 mb-4 ${i < currentIdx ? "bg-emerald-500" : "bg-slate-200"}`}/>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

